import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const formatCurrency = (n, decimals = 2) => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals,
  });
  try {
    return formatter.format(n);
  } catch (e) {
    return `$${Number(n).toFixed(decimals)}`;
  }
};

const InvestedAssetsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState('YTD'); // '1M' | '3M' | '6M' | '1Y' | 'YTD' | 'ALL'
  const [deltaMode, setDeltaMode] = useState('%'); // '%' | '$'
  const [viewMode, setViewMode] = useState('tiles'); // 'tiles' | 'table'

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/invested_assets/accounts/current_values/');
      const arr = res.data?.accounts || [];
      setAccounts(arr);
      setError(null);
    } catch (e) {
      console.error('Failed to load accounts list', e);
      setError('Failed to load investment accounts.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get('/api/invested_assets/accounts/values/history/');
      setHistory(res.data || []);
    } catch (e) {
      console.error('Failed to load accounts history', e);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadHistory();
  }, []);

  const totalMV = useMemo(() => {
    return accounts.reduce((sum, acc) => sum + (parseFloat(acc.current_value) || 0), 0);
  }, [accounts]);

  const getDelta = (accountId) => {
    const accHistory = history.find(h => h.id === accountId)?.series || [];
    if (accHistory.length === 0) return null;

    const getValAt = (series, dateStr) => {
      const found = series.find(p => p.date === dateStr);
      if (found) return found.value;
      const pts = series.filter(p => p.date <= dateStr);
      if (pts.length > 0) {
        // Find the LATEST point among those on or before dateStr
        const sorted = [...pts].sort((a, b) => b.date.localeCompare(a.date));
        return sorted[0].value;
      }
      return 0;
    };

    const now = new Date();
    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const currentMonth = ym(now);

    const subtractMonths = (dateStr, n) => {
      const d = new Date(dateStr);
      d.setMonth(d.getMonth() - n);
      return ym(d);
    };

    let baselineMonth = currentMonth;
    switch (period) {
      case '1M': baselineMonth = subtractMonths(currentMonth, 1); break;
      case '3M': baselineMonth = subtractMonths(currentMonth, 3); break;
      case '6M': baselineMonth = subtractMonths(currentMonth, 6); break;
      case '1Y': baselineMonth = subtractMonths(currentMonth, 12); break;
      case 'YTD': {
        baselineMonth = `${now.getFullYear() - 1}-12-01`;
        break;
      }
      case 'ALL': {
        baselineMonth = accHistory[0].date;
        break;
      }
      default: baselineMonth = currentMonth;
    }

    const currentVal = getValAt(accHistory, currentMonth);
    const baselineVal = getValAt(accHistory, baselineMonth);
    const diff = currentVal - baselineVal;

    if (deltaMode === '$') {
      const absDiff = Math.abs(diff);
      const sign = diff > 0 ? '+' : (diff < 0 ? '-' : '');
      return `${sign}${formatCurrency(absDiff, 0)}`;
    } else {
      if (baselineVal === 0) return diff === 0 ? '0.0%' : '∞%';
      const pct = (diff / baselineVal) * 100;
      const sign = pct > 0 ? '+' : (pct < 0 ? '-' : '');
      return `${sign}${Math.abs(pct).toFixed(1)}%`;
    }
  };

  const totalDelta = useMemo(() => {
    if (history.length === 0) return null;

    const now = new Date();
    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const currentMonth = ym(now);

    const subtractMonths = (dateStr, n) => {
      const d = new Date(dateStr);
      d.setMonth(d.getMonth() - n);
      return ym(d);
    };

    let baselineMonth = currentMonth;
    switch (period) {
      case '1M': baselineMonth = subtractMonths(currentMonth, 1); break;
      case '3M': baselineMonth = subtractMonths(currentMonth, 3); break;
      case '6M': baselineMonth = subtractMonths(currentMonth, 6); break;
      case '1Y': baselineMonth = subtractMonths(currentMonth, 12); break;
      case 'YTD': {
        baselineMonth = `${now.getFullYear() - 1}-12-01`;
        break;
      }
      case 'ALL': {
        const allDates = history.flatMap(h => h.series.map(p => p.date)).sort();
        baselineMonth = allDates[0] || currentMonth;
        break;
      }
      default: baselineMonth = currentMonth;
    }

    const getValAt = (series, dateStr) => {
      const found = series.find(p => p.date === dateStr);
      if (found) return found.value;
      const pts = series.filter(p => p.date <= dateStr);
      if (pts.length > 0) {
        // Find the LATEST point among those on or before dateStr
        const sorted = [...pts].sort((a, b) => b.date.localeCompare(a.date));
        return sorted[0].value;
      }
      return 0;
    };

    let currentSum = 0;
    let baselineSum = 0;

    history.forEach(h => {
      currentSum += getValAt(h.series, currentMonth);
      baselineSum += getValAt(h.series, baselineMonth);
    });

    const diff = currentSum - baselineSum;

    if (deltaMode === '$') {
      const absDiff = Math.abs(diff);
      const sign = diff > 0 ? '+' : (diff < 0 ? '-' : '');
      return `${sign}${formatCurrency(absDiff, 0)}`;
    } else {
      if (baselineSum === 0) return diff === 0 ? '0.0%' : '∞%';
      const pct = (diff / baselineSum) * 100;
      const sign = pct > 0 ? '+' : (pct < 0 ? '-' : '');
      return `${sign}${Math.abs(pct).toFixed(1)}%`;
    }
  }, [history, period, deltaMode]);

  return (
    <div className="page invested-assets-page">
      <div className="page-header-row" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <div className="header-title-container">
          <h1 style={{ margin: 0 }}>Invested Assets</h1>
        </div>
        <div className="header-ticker" style={{ display: 'flex', alignItems: 'baseline', marginLeft: '24px', paddingLeft: '24px', borderLeft: '1px solid #444' }}>
          <div className="header-metric" style={{ marginRight: '20px' }}>
            <span className="metric-label" style={{ fontSize: '14px', color: '#bbbbbb', marginRight: '8px' }}>Total Market Value</span>
            <span className="metric-value" style={{ fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(totalMV, 0)}</span>
          </div>
          <div className="header-metric">
            <span className="metric-label" style={{ fontSize: '14px', color: '#bbbbbb', marginRight: '8px' }}>Change ({period})</span>
            <span className={`metric-value delta-${(totalDelta || '').startsWith('+') ? 'up' : (totalDelta || '').startsWith('-') ? 'down' : 'neutral'}`} style={{ fontSize: '20px', fontWeight: 600 }}>
              {totalDelta || '—'}
            </span>
          </div>
        </div>
        <div className="header-breadcrumb-container" style={{ marginLeft: 'auto' }}>
          <Link to="/balance_sheet/assets/" style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '14px' }}>All Assets</Link>
        </div>
      </div>

      <div className="controls-row" style={{ display: 'flex', gap: '24px', marginBottom: '20px', alignItems: 'center' }}>
        <div className="tab-bar">
          <button
            className={`tab-btn ${viewMode === 'tiles' ? 'active' : ''}`}
            onClick={() => setViewMode('tiles')}
          >Tiles</button>
          <button
            className={`tab-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >Table</button>
        </div>

        <div className="period-toggles" style={{ display: 'flex', background: '#1e1e1e', borderRadius: '6px', padding: '2px' }}>
          {['1M', '3M', '6M', '1Y', 'YTD', 'ALL'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '6px 12px',
                border: 'none',
                background: period === p ? '#3b82f6' : 'transparent',
                color: period === p ? '#fff' : '#aaa',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="mode-toggles" style={{ display: 'flex', background: '#1e1e1e', borderRadius: '6px', padding: '2px' }}>
          {['%', '$'].map(m => (
            <button
              key={m}
              onClick={() => setDeltaMode(m)}
              style={{
                padding: '6px 12px',
                border: 'none',
                background: deltaMode === m ? '#3b82f6' : 'transparent',
                color: deltaMode === m ? '#fff' : '#aaa',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ color: 'tomato', marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {viewMode === 'table' && (
            <div className="card" style={{ marginTop: 10, background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '20px' }}>
              <div className="table-responsive">
                <table className="table table-striped" style={{ width: '100%', borderCollapse: 'collapse', color: '#ffffff' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #444', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px' }}>Brokerage</th>
                      <th style={{ padding: '12px 8px' }}>Type</th>
                      <th style={{ padding: '12px 8px' }}>Account #</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Market Value</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Change ({period})</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Return</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Benchmark</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Alpha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(acc => {
                      const delta = getDelta(acc.id);
                      const deltaClass = (delta || '').startsWith('+') ? 'delta-up' : (delta || '').startsWith('-') ? 'delta-down' : 'delta-neutral';
                      const accHistory = history.find(h => h.id === acc.id) || {};
                      const portRet = (accHistory.returns || {})[period];
                      const benchRet = (accHistory.benchmark_returns || {})[period];
                      const alpha = (portRet !== undefined && benchRet !== undefined && benchRet !== null) ? portRet - benchRet : null;

                      return (
                        <tr key={acc.account_number} style={{ borderBottom: '1px solid #2a2a2a' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <Link to={`/balance_sheet/assets/invested_assets/${acc.id || 1}`} style={{ color: '#60a5fa', textDecoration: 'none' }}>
                              {acc.brokerage || 'Investment Account'}
                            </Link>
                          </td>
                          <td style={{ padding: '12px 8px', color: '#aaa' }}>{acc.account_type || 'Brokerage'}</td>
                          <td style={{ padding: '12px 8px', color: '#aaa' }}>*{String(acc.account_number).slice(-4)}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatCurrency(acc.current_value, 0)}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }} className={deltaClass}>{delta || '—'}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }} className={portRet > 0 ? 'delta-up' : portRet < 0 ? 'delta-down' : 'delta-neutral'}>
                            {portRet !== undefined ? `${(portRet * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }} className={benchRet > 0 ? 'delta-up' : benchRet < 0 ? 'delta-down' : 'delta-neutral'}>
                            {benchRet !== undefined && benchRet !== null ? `${(benchRet * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }} className={alpha > 0 ? 'delta-up' : alpha < 0 ? 'delta-down' : 'delta-neutral'}>
                            {alpha !== null ? `${(alpha * 100).toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 'bold', borderTop: '2px solid #444' }}>
                      <td style={{ padding: '12px 8px' }}>Total</td>
                      <td></td>
                      <td></td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatCurrency(totalMV, 0)}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }} className={(totalDelta || '').startsWith('+') ? 'delta-up' : (totalDelta || '').startsWith('-') ? 'delta-down' : 'delta-neutral'}>
                        {totalDelta || '—'}
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {viewMode === 'tiles' && (
            <div className="tiles-container">
          <div className="tiles-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {accounts.map(acc => {
              const delta = getDelta(acc.id);
              const deltaClass = (delta || '').startsWith('+') ? 'delta-up' : (delta || '').startsWith('-') ? 'delta-down' : 'delta-neutral';

              return (
                <Link key={acc.account_number} to={`/balance_sheet/assets/invested_assets/${acc.id || 1}`} className="tile-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="tile-card" style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '20px', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                    <div className="tile-icon" style={{ fontSize: '32px', marginBottom: '12px' }}>📈</div>
                    <div className="tile-title" style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>{acc.brokerage || 'Investment Account'}</div>
                    <div className="tile-subtitle" style={{ fontSize: '14px', color: '#aaaaaa', marginBottom: '16px' }}>{acc.account_type || 'Brokerage'} *{String(acc.account_number).slice(-4)}</div>
                    <div className="tile-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      <div>
                        <span className="metric-label" style={{ display: 'block', fontSize: '10px', color: '#bbbbbb', textTransform: 'uppercase' }}>MV</span>
                        <span className="metric-value" style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{formatCurrency(acc.current_value, 0)}</span>
                      </div>
                      <div>
                        <span className="metric-label" style={{ display: 'block', fontSize: '10px', color: '#bbbbbb', textTransform: 'uppercase' }}>Change</span>
                        <span className={`metric-value ${deltaClass}`} style={{ display: 'block', fontSize: '14px', fontWeight: 600 }}>
                          {delta || '—'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span className="metric-label" style={{ display: 'block', fontSize: '10px', color: '#bbbbbb', textTransform: 'uppercase', marginBottom: '4px' }}>Return</span>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '8px', fontSize: '11px', lineHeight: '1.2' }}>
                          <span style={{ color: '#aaaaaa' }}>Port:</span>
                          <span className={((history.find(h => h.id === acc.id)?.returns || {})[period] || 0) > 0 ? 'delta-up' : ((history.find(h => h.id === acc.id)?.returns || {})[period] || 0) < 0 ? 'delta-down' : 'delta-neutral'} style={{ fontWeight: 600, textAlign: 'right' }}>
                            {history.find(h => h.id === acc.id)?.returns?.[period] !== undefined 
                              ? `${(history.find(h => h.id === acc.id).returns[period] * 100).toFixed(1)}%` 
                              : '—'}
                          </span>
                          
                          <span style={{ color: '#aaaaaa' }}>Bench:</span>
                          <span className={((history.find(h => h.id === acc.id)?.benchmark_returns || {})[period] || 0) > 0 ? 'delta-up' : ((history.find(h => h.id === acc.id)?.benchmark_returns || {})[period] || 0) < 0 ? 'delta-down' : 'delta-neutral'} style={{ fontWeight: 600, textAlign: 'right' }}>
                            {history.find(h => h.id === acc.id)?.benchmark_returns?.[period] !== undefined && history.find(h => h.id === acc.id)?.benchmark_returns?.[period] !== null
                              ? `${(history.find(h => h.id === acc.id).benchmark_returns[period] * 100).toFixed(1)}%` 
                              : '—'}
                          </span>

                          <span style={{ color: '#aaaaaa' }}>Alpha:</span>
                          {(() => {
                            const portRet = (history.find(h => h.id === acc.id)?.returns || {})[period];
                            const benchRet = (history.find(h => h.id === acc.id)?.benchmark_returns || {})[period];
                            if (portRet !== undefined && benchRet !== undefined && benchRet !== null) {
                              const alpha = portRet - benchRet;
                              const alphaClass = alpha > 0 ? 'delta-up' : alpha < 0 ? 'delta-down' : 'delta-neutral';
                              return (
                                <span className={alphaClass} style={{ fontWeight: 600, textAlign: 'right' }}>
                                  {(alpha * 100).toFixed(1)}%
                                </span>
                              );
                            }
                            return <span style={{ color: '#9ca3af', textAlign: 'right' }}>—</span>;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          {accounts.length === 0 && <p>No investment accounts found.</p>}
        </div>
      )}
    </>
  )}

      <style jsx>{`
        .invested-assets-page {
          margin-left: clamp(20px, 4vw, 40px);
          margin-right: clamp(20px, 4vw, 40px);
        }
        .tile-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        }
        .delta-up {
          color: #4ade80 !important;
        }
        .delta-down {
          color: #f87171 !important;
        }
        .delta-neutral {
          color: #9ca3af !important;
        }
        .tab-bar {
          display: inline-flex;
          align-items: flex-end;
          gap: 4px;
          border-bottom: 1px solid #374151;
        }
        .tab-btn {
          background: #111827;
          color: #e5e7eb;
          border: 1px solid #374151;
          border-bottom-color: #374151;
          padding: 6px 14px;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        .tab-btn:hover {
          background: #1f2937;
        }
        .tab-btn.active {
          background: #1e1e1e;
          border-color: #4b5563;
          border-bottom-color: transparent;
          color: #ffffff;
          position: relative;
          top: 1px;
        }
        .period-toggles button:hover, .mode-toggles button:hover {
          color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default InvestedAssetsPage;
